"use client";

// Vio — the copilot character — floating, draggable, expressive, a drop
// target, and on this site, jurisdiction-aware.
//
// WHAT MAKES THIS THE GLOBAL VERSION. The India site can assume one body of
// law. This product serves many countries and holds real depth in a few, and
// that asymmetry is the honest story rather than something to paper over: the
// backend's resolver returns "unknown" as a REAL answer and reads such
// contracts as general commercial agreements instead of borrowing another
// country's law. So Vio guesses the visitor's country from their browser
// locale and says what it can actually do there — verify against an official
// source, hold the law but not verify, or neither — sourced from
// `/api/jurisdictions`, which proxies the backend's own catalogue.
//
// It is built off the live catalogue and not a list in this file ON PURPOSE. A
// hardcoded coverage map is a claim that goes stale the day someone publishes
// a jurisdiction, and this site carries a claims guard
// (src/lib/copy/noUnbackedClaims.test.ts) because its copy has outrun the
// product twice already. Coverage copy that reads itself from the product
// cannot drift from it.
//
// The guess is stated as a guess — "Looks like Germany" — because it comes
// from a language tag, which is not where somebody is. Getting it wrong should
// read as a friendly miss, never as a confident assertion about the reader.
//
// THE ARTWORK IS AN SVG, NOT THE PNG IT WAS TRACED FROM. The character used to
// be `/vio-robot.png`, and a bitmap face cannot look at anything. Eyes that
// follow the cursor, a head that leans toward the paragraph you just selected,
// and eight expressions a visitor can tell apart all need addressable parts.
// This file owns everything that decides WHICH of those Vio is wearing;
// `vio/states.ts` owns what each one is allowed to mean, and the `.vio-svg`
// block in globals.css owns how each one looks.
//
// THE SAME RULE THAT GOVERNS THE COPY GOVERNS THE ANIMATION. `scanning` is not
// played as a flourish after a drop; it is subscribed to the scanner's actual
// in-flight request (lib/vio/handoff.ts::onScanning). An expression asserting
// work that is not happening is the same class of problem as a coverage claim
// nobody can back — it is just harder to grep for. What Vio does play on a
// drop is `success`, which is true: the text was accepted and is on its way.
//
// WHAT VIO ACCEPTS is text, not files. The public scanner is a TEXT endpoint,
// 100–30,000 characters, and `/scan` already refuses a dropped file with
// "File upload needs a free account". A mascot that swallowed a PDF and
// produced a signup wall would be worse than one that does nothing.
//
// Placement follows CookieConsent: bottom-right, z-index 9200 — below the
// cinematic intro (z 10000) and the custom cursor (z 10009/10010). The box
// itself is `.vio-wrap` in globals.css; only the values that change at
// runtime are set inline here.
//
// WHERE VIO IS NOT. This is the MARKETING Vio. It is mounted once, in the
// root layout, because the marketing pages have no route group of their own
// to hang a layout on — which also put it over the authenticated product
// (dashboard, vault, matters …), where the real Vio is the copilot at
// /assistant and a second, draggable one guessing your country from your
// browser locale is noise. The default export below is a gate on the
// pathname: inside the product shell it renders nothing. "The product
// shell" is `lib/auth/publicRoutes.ts::isProductPath` — the same predicate
// the middleware protects with — not a list kept here, so a new (app) route
// is mascot-free without anyone remembering this file. The gate is keyed on
// the path and never on a session: a signed-in person reading /pricing is
// on the marketing site and gets the marketing Vio.

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { HOLD_MS, STATE_LABEL, tracksCursor, type VioState } from "@/components/vio/states";
import VioCharacter from "@/components/vio/VioCharacter";
import { isProductPath } from "@/lib/auth/publicRoutes";
import {
  type Coverage,
  coverageLine,
  EMPTY_COVERAGE,
  guessCountry,
} from "@/lib/vio/coverage";
import {
  isPlainTextFile,
  MAX_CHARS,
  MIN_CHARS,
  onScanning,
  put,
} from "@/lib/vio/handoff";

const HREF = "/scan";
const DWELL_MS = 5200;
const DISMISS_KEY = "lexvio:vio-bubble-dismissed";
const MARGIN = 8;
const NUDGE = 24;

// A selection has to be long enough to plausibly be a clause before Vio
// reacts to it. Forty characters is under the scanner's own 100-character
// floor on purpose — Vio should notice a highlighted sentence and offer to
// read it, and then say why it cannot if the visitor drops something short.
// Reacting at one character would mean the character twitched every time
// anyone double-clicked a word.
const SELECTION_MIN = 40;

// How far Vio's head tilts toward a selection, and how long the pointer has
// to stay still before the eyes close into the reference arch.
const MAX_LEAN_DEG = 9;
const REST_AFTER_MS = 4500;

// Jurisdiction-neutral by construction. Nothing here names a country, a
// statute or a court, because this site serves all of them and none of them by
// default — §1A of docs/GLOBAL_BACKEND_PLAN_2026-08.md. The country-specific
// line is added at runtime from live coverage, or not at all.
const DEFAULT_LINES = [
  "Hi — I’m Vio.",
  "Select a clause and drag it onto me. I’ll scan it.",
  "I read the governing-law clause before deciding which law applies.",
  "Where I don’t hold local law, I say so and read it as a general commercial contract.",
  "If I can’t verify something, I say so rather than guess.",
] as const;

const ROUTE_LINES: ReadonlyArray<readonly [string, readonly string[]]> = [
  [
    "/scan",
    [
      "Paste a contract, or drag one onto me.",
      "I read up to 30,000 characters in one pass.",
      "Three free scans a day, no account needed.",
    ],
  ],
  [
    "/features",
    [
      "Hi — I’m Vio.",
      "Drag any clause on this page onto me and I’ll scan it.",
      "If I can’t verify something, I say so rather than guess.",
    ],
  ],
];

function linesFor(pathname: string | null): readonly string[] {
  if (!pathname) return DEFAULT_LINES;
  let best: readonly string[] | null = null;
  let bestLen = -1;
  for (const [prefix, lines] of ROUTE_LINES) {
    if (pathname.startsWith(prefix) && prefix.length > bestLen) {
      best = lines;
      bestLen = prefix.length;
    }
  }
  return best ?? DEFAULT_LINES;
}

function dragHasContent(types: readonly string[] | undefined): boolean {
  if (!types) return false;
  return types.some((t) => t === "Files" || t.startsWith("text/"));
}

const clampNum = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

export default function VioFab() {
  const pathname = usePathname();
  // Decided here, outside the character, because the character holds two
  // dozen hooks and an early return among them would break the rules of
  // hooks. A separate component makes "does not render" also mean "does not
  // subscribe" — no drag listeners, no coverage fetch, no selection watcher
  // inside the product. `usePathname` is a string on the app router; the
  // null branch is fail-closed for the same reason the middleware is.
  if (!pathname || isProductPath(pathname)) return null;
  return <MarketingVio />;
}

function MarketingVio() {
  const router = useRouter();
  const pathname = usePathname();

  const [ready, setReady] = useState(false);
  const [i, setI] = useState(0);
  const [bubble, setBubble] = useState(true);
  const [reduced, setReduced] = useState(false);
  // A transient line that outranks the rotation — what Vio says in response to
  // something you did, rather than what it says while idle.
  const [says, setSays] = useState<string | null>(null);
  const [armed, setArmed] = useState(false); // a drag is in flight somewhere
  const [over, setOver] = useState(false); // ...and it is over Vio
  const [hovering, setHovering] = useState(false);
  const [selecting, setSelecting] = useState(false); // a clause-sized selection exists
  const [busy, setBusy] = useState(false); // the scanner has a request in flight
  const [coverage, setCoverage] = useState<Coverage>(EMPTY_COVERAGE);
  const [country, setCountry] = useState<string | null>(null);

  // A state Vio is holding on purpose, with a timer behind it. It outranks
  // every derived state below, which is what makes "I could not read that"
  // survive the visitor moving their mouse away.
  const [held, setHeld] = useState<VioState | null>(null);
  // Eyes closed into the reference arch: no pointer, or none that has moved
  // recently. Starts true so the first paint is the face people recognise.
  const [rest, setRest] = useState(true);

  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const restRef = useRef(true);
  const timer = useRef<number | null>(null);
  const sayTimer = useRef<number | null>(null);
  const holdTimer = useRef<number | null>(null);
  const moved = useRef(false);
  const start = useRef<{ px: number; py: number; left: number; top: number } | null>(null);

  const base = linesFor(pathname);
  // The coverage line rides in second position — after the introduction, before
  // the generic capabilities — so the most specific true thing Vio knows about
  // this reader is the second thing it says. Null when the country is unknown
  // or uncovered, in which case the neutral lines already tell the truth.
  const jurisdictionLine = coverageLine(country, coverage);
  const lines = jurisdictionLine
    ? [base[0]!, jurisdictionLine, ...base.slice(1)]
    : base;

  // ── Which face Vio is wearing ───────────────────────────────────────────
  // Highest priority first. `held` beats everything because it is the answer
  // to something the visitor just did; `busy` beats the ambient states
  // because a scan genuinely in flight is the most important thing on the
  // page; hover beats selection so that reaching for Vio always gets a
  // greeting rather than a raised eyebrow.
  const state: VioState = held
    ? held
    : busy
      ? "scanning"
      : over || armed
        ? "curious"
        : hovering
          ? "happy"
          : selecting
            ? "curious"
            : "idle";

  const hold = useCallback((next: VioState) => {
    setHeld(next);
    if (holdTimer.current) window.clearTimeout(holdTimer.current);
    const ms = HOLD_MS[next];
    if (ms) holdTimer.current = window.setTimeout(() => setHeld(null), ms);
  }, []);

  // Reset the rotation when the route changes, adjusting state DURING RENDER —
  // React's documented way to reset state on a prop change, and what
  // `react-hooks/set-state-in-effect` pushes you toward.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setI(0);
  }

  const say = useCallback((message: string, ms = 4600) => {
    setSays(message);
    setBubble(true);
    if (sayTimer.current) window.clearTimeout(sayTimer.current);
    sayTimer.current = window.setTimeout(() => setSays(null), ms);
  }, []);

  const clamp = useCallback((left: number, top: number) => {
    const el = wrapRef.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    return {
      left: Math.min(Math.max(left, MARGIN), window.innerWidth - w - MARGIN),
      top: Math.min(Math.max(top, MARGIN), window.innerHeight - h - MARGIN),
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);

    // Reads happen inside the timeout, not the effect body: `sessionStorage`
    // and `matchMedia` do not exist during SSR so they cannot move into lazy
    // useState initialisers either.
    const t = window.setTimeout(() => {
      let dismissed = false;
      try {
        dismissed = sessionStorage.getItem(DISMISS_KEY) === "1";
      } catch {
        // Private mode / blocked storage — treat as "not dismissed".
      }
      if (dismissed) setBubble(false);
      setReduced(mq.matches);
      setCountry(guessCountry());
      setReady(true);
    }, 1900);

    return () => {
      window.clearTimeout(t);
      mq.removeEventListener("change", onChange);
      if (sayTimer.current) window.clearTimeout(sayTimer.current);
      if (holdTimer.current) window.clearTimeout(holdTimer.current);
    };
  }, []);

  // The scanner tells Vio when a request is genuinely in flight. This is the
  // only thing that puts Vio into `scanning`.
  useEffect(() => onScanning(setBusy), []);

  // Coverage is fetched once, after Vio is on screen, and failure is silent by
  // design: with EMPTY_COVERAGE every tier lookup misses, `coverageLine`
  // returns null, and Vio falls back to lines that are true everywhere. A
  // degraded network must never be able to produce a coverage claim.
  useEffect(() => {
    if (!ready) return;
    const ac = new AbortController();
    fetch("/api/jurisdictions", { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setCoverage({ adapters: d.adapters ?? [], depth: d.depth ?? [], supported: d.supported ?? [] });
      })
      .catch(() => {
        // Offline, aborted, or upstream down. Stay generic.
      });
    return () => ac.abort();
  }, [ready]);

  useEffect(() => {
    if (!ready || !bubble || reduced || says) return;
    timer.current = window.setInterval(
      () => setI((n) => (n + 1) % lines.length),
      DWELL_MS,
    );
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [ready, bubble, reduced, says, lines.length]);

  // ── Looking at the cursor ────────────────────────────────────────────────
  // Written straight onto the wrapper as CSS custom properties rather than
  // through React state. At 60fps a setState per pointermove would re-render
  // the whole subtree sixty times a second to move two ellipses four pixels;
  // a style property write is one line the compositor already knows how to
  // interpolate. rAF-batched so a mouse reporting at 1000Hz still costs one
  // write per frame.
  const tracking = ready && !reduced && tracksCursor(state) && !dragging;
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    // No fine pointer (a phone, a TV) means there is nothing to look at, and
    // an open eye tracking a cursor that does not exist reads as a stare. No
    // setRest is needed on that branch: `rest` starts true and only `onMove`
    // below ever clears it, so on a coarse-pointer device it never leaves the
    // resting arch in the first place.
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (!fine || !tracking) {
      el.style.setProperty("--vio-x", "0");
      el.style.setProperty("--vio-y", "0");
      return;
    }

    let frame = 0;
    let restAt = 0;
    let px = 0;
    let py = 0;

    const apply = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      // Vio's eyes sit in the upper part of the wrapper, below the bubble.
      const cy = r.bottom - r.height * 0.28;
      // Normalised against a third of the viewport, so the eyes reach their
      // limit at conversational distance and then hold — a linear map over
      // the whole page would leave them almost centred for most of it.
      const nx = clampNum((px - cx) / (window.innerWidth / 3), -1, 1);
      const ny = clampNum((py - cy) / (window.innerHeight / 3), -1, 1);
      el.style.setProperty("--vio-x", nx.toFixed(3));
      el.style.setProperty("--vio-y", ny.toFixed(3));
    };

    // `restRef` mirrors `rest` so the hot path can decide whether a state
    // update is needed at all. Calling setRest(false) unconditionally here
    // would queue a React render on every single pointermove — sixty a
    // second, to change nothing — which is the exact cost this effect writes
    // CSS properties to avoid.
    const onMove = (e: PointerEvent) => {
      px = e.clientX;
      py = e.clientY;
      if (restRef.current) {
        restRef.current = false;
        setRest(false);
      }
      window.clearTimeout(restAt);
      restAt = window.setTimeout(() => {
        restRef.current = true;
        setRest(true);
      }, REST_AFTER_MS);
      if (!frame) frame = requestAnimationFrame(apply);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.clearTimeout(restAt);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [tracking]);

  // ── Leaning toward what you selected ────────────────────────────────────
  // The single most useful thing Vio can do before anyone has interacted with
  // it: notice a highlighted clause and turn toward it. That is the whole
  // affordance — it says "this is draggable, and I am where it goes" without
  // a line of instructional copy.
  useEffect(() => {
    if (!ready) return;
    let debounce = 0;

    const read = () => {
      const el = wrapRef.current;
      if (!el) return;
      const sel = window.getSelection();
      const text = sel?.toString().trim() ?? "";

      if (!sel || sel.rangeCount === 0 || text.length < SELECTION_MIN) {
        setSelecting(false);
        el.style.setProperty("--vio-lean", "0");
        return;
      }

      // A collapsed or zero-area range (some browsers report one mid-drag)
      // has no position to lean toward, so leave the head where it is.
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (!rect.width && !rect.height) return;

      setSelecting(true);
      const r = el.getBoundingClientRect();
      const dx = rect.left + rect.width / 2 - (r.left + r.width / 2);
      // Negative rotation tilts the head toward the left of the screen, which
      // is where a selection almost always is relative to a bottom-right FAB.
      const lean = clampNum(
        (dx / (window.innerWidth / 2)) * MAX_LEAN_DEG,
        -MAX_LEAN_DEG,
        MAX_LEAN_DEG,
      );
      el.style.setProperty("--vio-lean", reduced ? "0" : lean.toFixed(2));
    };

    const onChange = () => {
      window.clearTimeout(debounce);
      // Long enough that dragging a selection across a paragraph does not
      // make the head chase the cursor word by word.
      debounce = window.setTimeout(read, 180);
    };

    document.addEventListener("selectionchange", onChange);
    return () => {
      document.removeEventListener("selectionchange", onChange);
      window.clearTimeout(debounce);
    };
  }, [ready, reduced]);

  // A window resized smaller can strand Vio off-screen, where it is
  // unreachable and un-draggable. Re-clamp instead.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clamp(p.left, p.top) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos, clamp]);

  // Light up whenever a drag starts anywhere, so the target is discoverable —
  // nobody drags a selection onto a mascot on a hunch.
  useEffect(() => {
    if (!ready) return;
    let depth = 0;
    const onEnter = (e: DragEvent) => {
      if (!dragHasContent(Array.from(e.dataTransfer?.types ?? []))) return;
      depth += 1;
      setArmed(true);
    };
    const onLeave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setArmed(false);
    };
    const onEnd = () => {
      depth = 0;
      setArmed(false);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onEnd);
    window.addEventListener("dragend", onEnd);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onEnd);
      window.removeEventListener("dragend", onEnd);
    };
  }, [ready]);

  function handOff(text: string) {
    const clean = text.trim();
    if (clean.length < MIN_CHARS) {
      hold("warning");
      say(`That’s ${clean.length} characters — I need at least ${MIN_CHARS} to say anything useful.`);
      return;
    }
    put(clean.slice(0, MAX_CHARS));
    if (clean.length > MAX_CHARS) {
      hold("warning");
      say(`Taking the first ${MAX_CHARS.toLocaleString()} characters — that’s my limit.`, 5600);
    } else {
      hold("success");
      say("Got it. Taking you to the scanner.", 3200);
    }
    router.push(HREF);
  }

  async function onDrop(e: ReactDragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    setArmed(false);

    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length) {
      const file = dropped[0]!;
      if (!isPlainTextFile(file)) {
        // The honest answer. The free scanner has no file path at all, so
        // pretending otherwise would end at a signup wall.
        hold("warning");
        say("I can’t open that format here — paste the text in and I’ll read it.", 6000);
        return;
      }
      try {
        // Reading a file off disk is the one genuinely slow step here, and it
        // is real work, so it is the one place `thinking` is truthful.
        hold("thinking");
        handOff(await file.text());
      } catch {
        hold("error");
        say("I couldn’t read that file. Paste the text in instead.");
      }
      return;
    }

    const text = e.dataTransfer.getData("text/plain");
    if (text) {
      handOff(text);
      return;
    }
    hold("warning");
    say("Nothing came across — try dragging a selection of the text.");
  }

  function onPointerDown(e: ReactPointerEvent) {
    if (e.button !== 0) return;
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    start.current = { px: e.clientX, py: e.clientY, left: r.left, top: r.top };
    moved.current = false;
    setDragging(true);
    // Capture so the drag survives the pointer leaving the character — without
    // it, moving faster than React re-renders drops the drag.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent) {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.px;
    const dy = e.clientY - s.py;
    // 4px of slop, so a click with a shaky hand is still a click.
    if (!moved.current && Math.abs(dx) + Math.abs(dy) < 4) return;
    moved.current = true;
    setPos(clamp(s.left + dx, s.top + dy));
  }

  function onPointerUp(e: ReactPointerEvent) {
    start.current = null;
    setDragging(false);
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // Capture may already be gone if the pointer was cancelled.
    }
  }

  // Pointer dragging is unavailable to a keyboard user, so arrow keys nudge
  // instead. Without this, someone whose Vio covers the thing they are reading
  // has no way to move it — the link stays reachable, the obstruction does not.
  function onKeyDown(e: ReactKeyboardEvent) {
    const step = e.shiftKey ? NUDGE * 3 : NUDGE;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = deltas[e.key];
    if (!d) return;
    e.preventDefault();
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(clamp(r.left + d[0], r.top + d[1]));
  }

  function dismiss() {
    setBubble(false);
    setSays(null);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Nothing to do — it reappears next page, which is acceptable.
    }
  }

  const message =
    says ??
    (busy
      ? "Reading it now."
      : over
        ? "Let go — I’ll take it from here."
        : armed
          ? "Drop it on me and I’ll scan it."
          : selecting
            ? "Drag that onto me and I’ll read it."
            : lines[i % lines.length]);

  // The state word carried in text as well as in the face. A screen reader
  // gets the bubble's live region, never the picture, so a state that only
  // existed as an expression would not exist for that visitor at all.
  const tag = STATE_LABEL[state];

  return (
    <div
      ref={wrapRef}
      className={`vio-wrap${dragging ? " vio-dragging" : ""}${armed ? " vio-armed" : ""}${over ? " vio-over" : ""}`}
      data-state={state}
      style={{
        // Anchored bottom-right until dragged, then switched to left/top so
        // clamping is a straight comparison against the viewport box.
        ...(pos
          ? { left: `${pos.left}px`, top: `${pos.top}px` }
          : { right: "20px", bottom: "18px" }),
        opacity: ready ? 1 : 0,
        transform: ready ? "translateY(0)" : "translateY(10px)",
        transition: dragging
          ? "none"
          : "opacity .6s var(--eout, ease), transform .6s var(--eout, ease)",
        pointerEvents: ready ? "auto" : "none",
      }}
    >
      {bubble ? (
        <div
          // Announce line changes politely rather than interrupting; a
          // decorative mascot must never talk over what someone is reading.
          aria-live="polite"
          className="vio-bubble"
          role="status"
          style={{
            position: "relative",
            maxWidth: "268px",
            background: "rgba(251,250,246,.985)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid var(--b300, #DDD8C9)",
            borderRadius: "14px",
            padding: "12px 30px 12px 14px",
            boxShadow: "0 10px 34px rgba(19,18,16,.13)",
            fontFamily: "var(--font-dm), system-ui, sans-serif",
            // Heavier and darker than body copy on purpose. This sits over a
            // busy hero at 13px in a translucent panel; at 400 weight it read
            // as grey chrome rather than as something Vio is saying.
            fontSize: "13.5px",
            fontWeight: 600,
            letterSpacing: "-.004em",
            lineHeight: 1.5,
            color: "#131210",
          }}
        >
          <button
            aria-label="Dismiss Vio’s message"
            className="vio-x"
            style={{
              position: "absolute",
              top: "7px",
              right: "8px",
              width: "18px",
              height: "18px",
              lineHeight: "16px",
              textAlign: "center",
              fontSize: "13px",
              color: "var(--ink-subtle, #6B6860)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            type="button"
            onClick={dismiss}
          >
            ×
          </button>

          <span key={`${tag}${message}`} className="vio-line">
            {tag ? <span className="vio-tag">{tag} · </span> : null}
            {message}
          </span>

          {/* Tail, pointing down-right at the character. */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right: "26px",
              bottom: "-7px",
              width: "12px",
              height: "12px",
              background: "rgba(251,250,246,.985)",
              borderRight: "1px solid var(--b300, #DDD8C9)",
              borderBottom: "1px solid var(--b300, #DDD8C9)",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      ) : null}

      <Link
        aria-describedby="vio-help"
        aria-label="Ask Vio — scan a contract free"
        className="vio-fab"
        draggable={false}
        href={HREF}
        // The one thing `.vio-fab` does not own: the cursor has to change with
        // a runtime flag, so it stays here rather than becoming a third class.
        style={{ cursor: dragging ? "grabbing" : "grab" }}
        onBlur={() => setHovering(false)}
        // A drag that ends on the character would otherwise also follow the
        // link. `moved` is set only past the 4px slop, so a plain click still
        // navigates.
        onClick={(e) => {
          if (moved.current) {
            e.preventDefault();
            moved.current = false;
          }
        }}
        onDragEnter={(e) => {
          if (!dragHasContent(Array.from(e.dataTransfer.types))) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDragOver={(e) => {
          if (!dragHasContent(Array.from(e.dataTransfer.types))) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragStart={(e) => e.preventDefault()}
        onDrop={onDrop}
        onFocus={() => setHovering(true)}
        onKeyDown={onKeyDown}
        onPointerCancel={onPointerUp}
        onPointerDown={onPointerDown}
        onPointerEnter={() => setHovering(true)}
        onPointerLeave={() => setHovering(false)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Four nested elements because one cannot carry four transform
            animations, and a state rule that sets `transform` on an element
            already animating it is silently overridden. Outer to inner:
            idle gesture (13s) → state motion → bob (5.5s) → sway (7.3s).
            The three idle periods are coprime-ish, so the combined motion
            takes about forty seconds to repeat and never reads as a loop.
            The contact shadow is drawn inside the SVG rather than as a
            sibling span, so it leans and scales with the character. */}
        <span className="vio-scale">
          <span className="vio-bob">
            <VioCharacter rest={rest && state === "idle"} size={128} state={state} />
          </span>
        </span>
      </Link>

      <span hidden id="vio-help">
        Drop selected contract text on Vio to scan it. Use the arrow keys to
        move Vio around the screen.
      </span>
    </div>
  );
}
